/* eslint-disable */
import AWS from 'aws-sdk'
import { Auth } from '@aws-amplify/auth'

/**
 * CognitoService - Direct AWS Cognito User Pool management
 * Handles user listing, updates, and deletions directly with Cognito
 */
class CognitoService {
    constructor() {
        this.userPoolId = process.env.VUE_APP_USER_POOL_ID
        this.region = process.env.VUE_APP_AWS_REGION

        // Disable AWS SDK logging
        AWS.config.logger = null
    }

    /**
     * Get authenticated Cognito client
     */
    async getCognitoClient() {
        try {
            const credentials = await Auth.currentCredentials()

            return new AWS.CognitoIdentityServiceProvider({
                region: this.region,
                credentials: Auth.essentialCredentials(credentials)
            })
        } catch (error) {
            console.error('Failed to get Cognito client:', error)
            throw new Error('Authentication failed. Please log in again.')
        }
    }

    /**
     * List all users from Cognito User Pool
     * @returns {Promise<Array>} Array of users with formatted data
     */
    async listUsers() {
        try {
            const cognito = await this.getCognitoClient()
            const params = {
                UserPoolId: this.userPoolId,
                Limit: 60 // Adjust as needed
            }

            const response = await cognito.listUsers(params).promise()

            // Format users to match existing structure
            return response.Users.map((user, index) => {
                const attributes = this.getUserAttributes(user.Attributes)
                return {
                    id: index + 1,
                    username: user.Username,
                    name: attributes['custom:name'] || '',
                    email_address: attributes.email || '',
                    department: attributes['custom:department'] || '',
                    role: attributes['custom:role'] || '',
                    extension: attributes['custom:extension'] || '',
                    region: attributes['custom:region'] || '',
                    status: user.UserStatus,
                    enabled: user.Enabled
                }
            })
        } catch (error) {
            console.error('Failed to list users:', error)
            throw new Error(`Failed to fetch users: ${error.message}`)
        }
    }

    /**
     * List all groups in the Cognito User Pool
     * @returns {Promise<Array>} Array of group names
     */
    async listGroups() {
        try {
            const cognito = await this.getCognitoClient()
            const params = {
                UserPoolId: this.userPoolId,
                Limit: 60
            }

            const response = await cognito.listGroups(params).promise()
            return response.Groups.map(group => group.GroupName)
        } catch (error) {
            console.error('Failed to list groups:', error)
            throw new Error(`Failed to fetch groups: ${error.message}`)
        }
    }

    /**
     * Get user's current groups
     */
    async getUserGroups(username) {
        try {
            const cognito = await this.getCognitoClient()
            const params = {
                UserPoolId: this.userPoolId,
                Username: username
            }

            const response = await cognito.adminListGroupsForUser(params).promise()
            return response.Groups.map(group => group.GroupName)
        } catch (error) {
            console.error('Failed to get user groups:', error)
            return []
        }
    }

    /**
     * Update user attributes and group membership
     * @param {string} username - Cognito username
     * @param {Object} userData - User data to update
     */
    async updateUser(username, userData) {
        try {
            const cognito = await this.getCognitoClient()

            // Update user attributes
            const attributes = []
            if (userData.name) attributes.push({ Name: 'custom:name', Value: userData.name })
            if (userData.email_address) attributes.push({ Name: 'email', Value: userData.email_address })
            if (userData.department) attributes.push({ Name: 'custom:department', Value: userData.department })
            if (userData.role) attributes.push({ Name: 'custom:role', Value: userData.role })
            if (userData.extension) attributes.push({ Name: 'custom:extension', Value: userData.extension })
            if (userData.region) attributes.push({ Name: 'custom:region', Value: userData.region })

            if (attributes.length > 0) {
                await cognito.adminUpdateUserAttributes({
                    UserPoolId: this.userPoolId,
                    Username: username,
                    UserAttributes: attributes
                }).promise()
            }

            // Update group membership if role changed
            if (userData.role) {
                await this.updateUserGroup(username, userData.role)
            }

            return { success: true }
        } catch (error) {
            console.error('Failed to update user:', error)
            throw new Error(`Failed to update user: ${error.message}`)
        }
    }

    /**
     * Update user's Cognito group based on role
     * Removes user from all groups and adds to new group
     */
    async updateUserGroup(username, newRole) {
        try {
            const cognito = await this.getCognitoClient()

            // Get current groups
            const currentGroups = await this.getUserGroups(username)

            // Remove from all current groups
            for (const groupName of currentGroups) {
                await cognito.adminRemoveUserFromGroup({
                    UserPoolId: this.userPoolId,
                    Username: username,
                    GroupName: groupName
                }).promise()
            }

            // Add to new group (group name = role)
            await cognito.adminAddUserToGroup({
                UserPoolId: this.userPoolId,
                Username: username,
                GroupName: newRole
            }).promise()

            return { success: true }
        } catch (error) {
            console.error('Failed to update user group:', error)
            throw new Error(`Failed to update user group: ${error.message}`)
        }
    }

    /**
     * Delete user from Cognito User Pool
     * @param {string} username - Cognito username
     */
    async deleteUser(username) {
        try {
            const cognito = await this.getCognitoClient()

            await cognito.adminDeleteUser({
                UserPoolId: this.userPoolId,
                Username: username
            }).promise()

            return { success: true }
        } catch (error) {
            console.error('Failed to delete user:', error)
            throw new Error(`Failed to delete user: ${error.message}`)
        }
    }

    /**
     * Helper to convert attributes array to object
     */
    getUserAttributes(attributes) {
        return attributes.reduce((acc, attr) => {
            acc[attr.Name] = attr.Value
            return acc
        }, {})
    }
}

export default new CognitoService()
